import React, { Suspense, useCallback, useEffect, useState } from 'react';
import bem from 'utils/bem';
import { observer } from 'mobx-react-lite';
import Model from '../../../../model';
import { useFormikContext } from 'formik';
import CheckboxField from 'forms/fields/CheckboxField';
import { Skeleton } from '@mui/material';
import './index.sass';
import RadioField from 'forms/fields/RadioField';
import PaymentBalanceField from 'forms/fields/PaymentBalanceField';
import { proceedToCheckout } from '../../../../model/utils';
import KZNotEnoughBalance from 'components/KZNotEnoughBalance';
import ProblemsAutocompleteField from 'forms/fields/ProblemsAutocompleteField';
import UserInfo from 'forms/forms/OrderForm/UserInfo';
import OrderInfo from 'forms/forms/OrderForm/OrderInfo';
import Tickets, { TicketsSkeleton } from 'forms/forms/SpecialistOrderForm/Tickets';
import Summary from 'forms/forms/OrderForm/Summary';
import { specialistEventsData } from 'api/events';
import Icon from 'components/Icon';
import Text from 'components/Text';
import { ForeignCardWidget } from 'components/ForeignCardWidget';
import { TextWithPulsingDot } from 'components/TextWithPulsingDot';
import { GreenText } from 'components/GreenText';

const { el } = bem('specialist-order-form', 'form');

const FirstStep = observer(({ resource }) => {
    resource.read();
    const {
        pairVisit,
        setPairVisit,
        offlineVisit,
        setOfflineVisit,
        specialist: { workingWithPairs, data: { onlineCount, offlineCount }, id: specId },
        hasSelectedEvents,
        resetSelectedEvents,
        amount,
        problem,
        setProblem
    } = Model.specialistOrderForm;

    const problemsOptions = Model.specialist.allProblems(specId);
    const { values: { comment }, setFieldValue } = useFormikContext();
    const setComment = useCallback((value) => {
        setFieldValue('comment', value);
    }, []);

    const onPairVisitChange = (e) => {
        // if (hasSelectedEvents) {
        //     resetSelectedEvents(amount, true);
        // }
        const value = e.target.checked;
        setPairVisit(value);
    };

    const onOfflineVisitChange = (e) => {
        // if (hasSelectedEvents) {
        //     resetSelectedEvents(amount, true);
        // }
        const value = e.target.value;
        setOfflineVisit(value === 'offline');
    };

    useEffect(() => {
        setFieldValue('workingOffline', !!offlineVisit);
    }, [ offlineVisit ])

    const problemsProps = {
        problemsOptions,
        problem,
        setProblem,
        setComment,
        comment
    }

    return (
        <div className={ el('first-step') }>
            <div className={ el('content-block') }>
                <div className={ el('fields') }>
                    <div className={ el('first-row') }>
                        <ProblemsAutocompleteField { ...problemsProps } />
                    </div>
                    <div className={ el('second-row') }>
                        { workingWithPairs &&
                            <CheckboxField name="pairVisit" text={ 'Консультация для пары' } checked={ pairVisit }
                                           onChange={ onPairVisitChange } /> }
                        <div className={ el('offline-radio-field') }>
                            <div className={ el('radio-option', { 'selected': offlineVisit === false }) }>
                                <RadioField
                                    name={ 'offline' }
                                    text={ <TextWithPulsingDot><GreenText>Онлайн</GreenText>-встреча</TextWithPulsingDot> }
                                    value={ 'online' }
                                    disabled={ onlineCount <= 0 || hasSelectedEvents }
                                    checked={ offlineVisit === false }
                                    onChange={ onOfflineVisitChange }
                                />
                            </div>
                            <div className={ el('radio-option', { 'selected': offlineVisit === true }) }>
                                <RadioField
                                    name={ 'offline' }
                                    text={ 'Очная встреча' }
                                    value={ 'offline' }
                                    disabled={ offlineCount <= 0 || hasSelectedEvents }
                                    checked={ offlineVisit === true }
                                    onChange={ onOfflineVisitChange }
                                />
                            </div>
                        </div>
                        {/*{ <CheckboxField name='offline' text={'Очная\nвстреча'} checked={offlineVisit} disabled={onlineCount <= 0 || offlineCount <= 0} onChange={onOfflineVisitChange}/> }*/ }
                    </div>
                    <div className={ el('third-row') }>
                        <div className={ el('hint') }>
                            <Icon name={ 'info' }/>
                            <Text>Если у специалиста доступен только один тип встречи, выбор типа встречи будет не активен</Text>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

const FirstStepSkeleton = () => {
    return (
        <div className={ el('first-step') }>
            <div className={ el('content-block') }>
                <div className={ el('fields') }>
                    <Skeleton width={ 150 } />
                </div>
            </div>
        </div>
    );
};

const ThirdStep = observer(({ goToFinalStep = () => {} }) => {
    const { specialist } = Model.specialistOrderForm;
    const resource = specialistEventsData({ specialist: specialist.id });
    return (
        <div className={ el('third-step') }>
            <div className={ el('content-block') }>
                <Suspense fallback={ <TicketsSkeleton /> }>
                    <TicketsBlock goToFinalStep={ goToFinalStep } resource={resource} />
                </Suspense>
            </div>
        </div>
    );
});


const TicketsBlock = observer(({ goToFinalStep, resource }) => {
    resource.read();
    const {
        specialist,
        selectedEvents,
        offlineVisit,
        setSelectedEvents,
        amount,
        setAmount,
        setTicketType,
        ticketOptions,
        specialistEvents: events
    } = Model.specialistOrderForm;

    const { setFieldValue } = useFormikContext();
    const onSaveClick = useCallback(() => {
        proceedToCheckout(() => {
            setFieldValue('events', selectedEvents.map(e => specialist.getPK(e, offlineVisit ? 'OFF' : 'ON')));
            goToFinalStep();
        });
    }, [ selectedEvents ]);

    const ticketsProps = {
        workingOffline: offlineVisit,
        specialist,
        selectedEvents,
        setSelectedEvents,
        amount,
        setAmount,
        setTicketType,
        onSaveClick,
        ticketOptions,
        events
    }

    return <Tickets { ...ticketsProps } />
});


const FinalStep = observer(({ setShowFinalStep }) => {
    const { pairVisit, offlineVisit, specialist } = Model.specialistOrderForm;
    const { values: { events, comment } } = useFormikContext();

    const checkoutScreenProps = {
        workingOffline: offlineVisit,
        specialist,
        events,
        comment,
        workingWithPairs: pairVisit
    }

    const onEditClick = () => {
        setShowFinalStep(false);
    };

    return <OrderInfo onEditClick={ onEditClick } { ...checkoutScreenProps } />
});

const UserFields = observer(() => {
    const { offlineVisit } = Model.specialistOrderForm;
    const { setFieldValue, values: { customerPhone } } = useFormikContext();
    const { isAuthenticated, username } = Model.user;

    const setPhone = useCallback((phone) => {
        setFieldValue('customerPhone', phone)
    }, []);

    const setCustomerFound = useCallback((isCustomerFound) => {
        setFieldValue('customerFound', isCustomerFound)
    }, []);

    const userInfoProps = {
        workingOffline: offlineVisit,
        customerPhone,
        isAuthenticated,
        username,
        setPhone,
        setCustomerFound
    }

    return <UserInfo { ...userInfoProps } />
});

const SummaryBlock = observer(() => {
    const { price, submitting } = Model.specialistOrderForm;
    const { kzNotEnoughBalance } = Model.kz;

    return <Summary price={ price } submitting={ submitting } kzNotEnoughBalance={ kzNotEnoughBalance(price) } />
});

const FirstPage = ({ goToFinalStep }) => {
    const { specialist } = Model.specialistOrderForm;
    const { oneSpecialistDetailsData } = Model.specialist;
    const getOneSpecialistDetails = oneSpecialistDetailsData(specialist.data.slug);

    return (
        <Suspense fallback={ <FormSkeleton /> }>
            <>
                <FirstStep resource={ getOneSpecialistDetails } />
                <ThirdStep goToFinalStep={ goToFinalStep } />
            </>
        </Suspense>
    );
};

const SecondPage = ({ setShowFinalStep }) => {
    const { price } = Model.specialistOrderForm;

    return (
        <>
            <FinalStep setShowFinalStep={ setShowFinalStep } />
            <UserFields />
            <PaymentBalanceField mod={ 'long' } name={ 'saleSum' } price={ price } />
            <KZNotEnoughBalance price={ price } />
            { window.isRuDomain && <ForeignCardWidget /> }
            <SummaryBlock />
        </>
    );
};

const Form = ({ handleSubmit }) => {
    const { specialist } = Model.specialistOrderForm;

    const [ showFinalStep, setShowFinalStep ] = useState(false);

    const goToFinalStep = useCallback(() => {
        setShowFinalStep(true);
    }, []);

    return (
        <form onSubmit={ handleSubmit }>
            { (!showFinalStep || !specialist) && <FirstPage goToFinalStep={ goToFinalStep } /> }
            { showFinalStep && specialist && <SecondPage setShowFinalStep={ setShowFinalStep } /> }
        </form>
    );
};

const FormSkeleton = () => {
    return (
        <div>
            <FirstStepSkeleton />
        </div>
    );
};

export default observer(Form);
